import { getShippedQuantityForOrderStrict } from "../utils/stockCalculations";
import { getOrderedQuantity } from "./purchaseOrderOpenItems";
import {
  PURCHASE_ORDER_STATUS_CANCELLED,
  PURCHASE_ORDER_STATUS_COMPLETED,
} from "../constants/OrderConstant";

/**
 * Is deze inkooporder klaar, en zo nee: waarom niet (BMX-14).
 *
 * Voedt twee dingen in de schermen:
 * - de kolom "Open" op het inkooporderoverzicht, zodat te zien is wáárom een order nog actief is;
 * - de knop "Afgeronde orders opruimen", die de achterstand wegwerkt van orders die allang klaar
 *   zijn maar nooit zijn afgerond.
 *
 * ── Waarom dit er twee keer staat ──
 *
 * De gezaghebbende versie van deze regel is `cloud-functions/src/shared/purchaseOrderStatus.js`;
 * die rondt orders vanaf nu automatisch af zodra er een verzending wijzigt. Dit bestand is
 * bewust een tweede implementatie en geen import, om een saaie reden: het CMS draait op
 * react-scripts, dat niet buiten `src/` mag importeren, en de cloud functions worden vanuit hun
 * eigen map gedeployed en kunnen niets van daarbuiten meenemen. Er is vandaag geen gedeelde
 * plek die allebei kunnen laden.
 *
 * Wat daar tegenover staat: de rekenregels zelf komen uit bestaande, geteste helpers
 * (`getShippedQuantityForOrderStrict`, `getOrderedQuantity`), dus alleen de beslissing eromheen
 * staat hier opnieuw — en die is aan beide kanten met dezelfde gevallen afgedekt in proeven.
 * Wijzig je de een, wijzig dan de ander.
 */

/** Verzendstatussen die niet als "verzonden" meetellen — gelijk aan de cloud-functionregel. */
const IGNORED_SHIPMENT_STATUSES = ["cancelled", "draft"];

/** De verzending is de goederenontvangst doorgelopen: geteld en verwerkt. */
const SHIPMENT_PROCESSED = "completed";

/** Statussen waar een order niet meer automatisch uit weg beweegt. */
export const TERMINAL_ORDER_STATUSES = [PURCHASE_ORDER_STATUS_COMPLETED, PURCHASE_ORDER_STATUS_CANCELLED];

/** De verzendingen die voor deze order meetellen. */
export const getShipmentsForOrder = (shipments, orderId) =>
  (shipments || []).filter(
    (shipment) =>
      shipment &&
      !IGNORED_SHIPMENT_STATUSES.includes(shipment.status) &&
      (shipment.sourceOrders || []).some((so) => so && so.purchaseOrderId === orderId),
  );

/**
 * Waar staat deze order: hoeveel stuks en regels staan er nog open, en zijn alle verzendingen al
 * verwerkt? `fullyProcessed` is precies de voorwaarde waarop de order afgerond mag worden.
 */
export const summarizeOrder = (order, shipments) => {
  const orderId = order?.id;
  const orderShipments = getShipmentsForOrder(shipments, orderId);
  const products = order?.products || [];

  let openQuantity = 0;
  let openLines = 0;
  let shippedQuantity = 0;

  products.forEach((product) => {
    const shipped = getShippedQuantityForOrderStrict(orderShipments, orderId, product.productId);
    const open = Math.max(0, getOrderedQuantity(product) - (product.cancelledAmount || 0) - shipped);
    openQuantity += open;
    if (open > 0) openLines += 1;
    shippedQuantity += shipped;
  });

  // Een order zonder regels is niet "klaar", die is leeg.
  const fullyShipped = products.length > 0 && openQuantity === 0;
  const unprocessedShipmentCount = orderShipments.filter((s) => s.status !== SHIPMENT_PROCESSED).length;

  return {
    openQuantity,
    openLines,
    shippedQuantity,
    fullyShipped,
    shipmentCount: orderShipments.length,
    unprocessedShipmentCount,
    fullyProcessed: fullyShipped && orderShipments.length > 0 && unprocessedShipmentCount === 0,
  };
};

/** Mag deze order afgerond worden? Afgerond/geannuleerd blijven staan. */
export const isReadyToComplete = (order, shipments) => {
  if (!order?.status || TERMINAL_ORDER_STATUSES.includes(order.status)) return false;
  return summarizeOrder(order, shipments).fullyProcessed;
};

/** De orders uit deze lijst die volgens bovenstaande regel afgerond kunnen worden. */
export const findOrdersReadyToComplete = (orders, shipments) =>
  (orders || []).filter((order) => isReadyToComplete(order, shipments));

/**
 * Eén zin die uitlegt waarom deze order nog in het actieve overzicht staat. Leeg als de order
 * niet meer actief is.
 */
export const describeWhyActive = (order, shipments) => {
  if (!order?.status || TERMINAL_ORDER_STATUSES.includes(order.status)) return "";

  const summary = summarizeOrder(order, shipments);
  // Volgorde is bewust: "er is nog niets verzonden" zegt meer dan het aantal open stuks, en
  // dekt meteen de orders die nog in productie of concept staan.
  if (summary.shipmentCount === 0) return "Nog geen verzending aangemaakt";
  if (summary.openQuantity > 0) {
    return `${summary.openQuantity} stuks op ${summary.openLines} ${
      summary.openLines === 1 ? "regel" : "regels"
    } nog niet verzonden`;
  }
  if (summary.unprocessedShipmentCount > 0) {
    return `${summary.unprocessedShipmentCount} ${
      summary.unprocessedShipmentCount === 1 ? "verzending" : "verzendingen"
    } nog niet verwerkt`;
  }
  return "Klaar om af te ronden";
};
